import { useEffect, useState } from "react";
import api from "@/lib/api";
import { flattenErrorText, flattenToast } from "@/lib/paperFlatten";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import OrdersTable from "@/components/paper/OrdersTable";
import PositionsTable from "@/components/paper/PositionsTable";
import BracketOrdersTable from "@/components/paper/BracketOrdersTable";
import LiveOrderTicket from "@/components/paper/LiveOrderTicket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldAlert, ArrowDownUp, Layers } from "lucide-react";
import { toast } from "sonner";
import MultiLegBuilder from "@/components/MultiLegBuilder";
import ChainSourceBadge from "@/components/ChainSourceBadge";
import { chainOrderBlock, chainSourceInfo, liveStrikes } from "@/lib/optionChain";

const fmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });


function BracketOrderPanel({ symbols, disabled, onPlaced }) {
  const [open, setOpen] = useState(false);
  const [symbol, setSymbol] = useState("NIFTY");
  const [symbolSearch, setSymbolSearch] = useState("");
  const [side, setSide] = useState("BUY");
  const [qty, setQty] = useState(50);
  const [stopLoss, setStopLoss] = useState("");
  const [target, setTarget] = useState("");
  const [trail, setTrail] = useState("");
  const [placing, setPlacing] = useState(false);

  const place = async () => {
    if (!stopLoss || !target) { toast.error("Stop loss and target required for bracket order"); return; }
    setPlacing(true);
    try {
      await api.post("/paper/order/bracket", {
        symbol,
        side,
        qty: Number(qty),
        stop_loss: Number(stopLoss),
        target_price: Number(target),
        ...(trail ? { trail_percent: Number(trail) } : {}),
      });
      toast.success(`Bracket ${side} ${qty} ${symbol} placed`);
      setStopLoss(""); setTarget(""); setTrail("");
      onPlaced?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bracket order failed");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-surface-2)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 txt-secondary" />
          <span className="overline">Bracket Order (Entry + SL + Target)</span>
        </div>
        <span className="text-xs txt-muted">{open ? "▲ Collapse" : "▼ Expand"}</span>
      </button>
      {open && (
        <div className="border-t border-[var(--border)] p-5">
          <div className="text-xs txt-secondary mb-4">
            A bracket order places your entry, stop-loss, and target simultaneously as an atomic unit.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div className="col-span-2 sm:col-span-1 lg:col-span-1">
              <Label className="overline">Symbol</Label>
              <div className="relative mt-2">
                <Input
                  value={symbolSearch || symbol}
                  onChange={(e) => setSymbolSearch(e.target.value)}
                  onFocus={() => setSymbolSearch("")}
                  placeholder="Symbol…"
                  className="rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data uppercase"
                />
                {symbolSearch && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-[var(--bg-surface)] border border-[var(--border)] max-h-36 overflow-auto">
                    {symbols.filter((s) => s.symbol.includes(symbolSearch.toUpperCase())).slice(0, 12).map((s) => (
                      <button key={s.symbol} className="w-full text-left px-3 py-1.5 text-xs font-mono-data hover:bg-[var(--bg-surface-2)]"
                        onMouseDown={() => { setSymbol(s.symbol); setSymbolSearch(""); }}>
                        {s.symbol}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="overline">Side</Label>
              <Select value={side} onValueChange={setSide}>
                <SelectTrigger className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)]">
                  <SelectItem value="BUY">BUY</SelectItem>
                  <SelectItem value="SELL">SELL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Qty</Label>
              <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
            </div>
            <div>
              <Label className="overline">Stop Loss ₹</Label>
              <Input type="number" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} placeholder="Required" className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data border-red-500/30" />
            </div>
            <div>
              <Label className="overline">Target ₹</Label>
              <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Required" className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data border-emerald-500/30" />
            </div>
            <div>
              <Label className="overline">Trail % (opt)</Label>
              <Input type="number" value={trail} onChange={(e) => setTrail(e.target.value)} placeholder="e.g. 0.5" className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
            </div>
          </div>
          <Button
            onClick={place}
            disabled={placing || disabled || !stopLoss || !target}
            className={`mt-4 rounded-none h-10 font-section tracking-wider ${side === "BUY" ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-red-500 hover:bg-red-400 text-white"}`}
          >
            <Layers className="w-4 h-4 mr-2" /> {placing ? "PLACING…" : `BRACKET ${side}`}
          </Button>
        </div>
      )}
    </div>
  );
}


export default function PaperExecution() {
  const [symbols, setSymbols] = useState([]);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [liveOrders, setLiveOrders] = useState([]);
  const [liveBrokers, setLiveBrokers] = useState([]);
  const [totals, setTotals] = useState({ total_pnl: 0, exposure: 0, net_exposure: null, bad_avg_price_count: 0, stale_count: 0 });
  const [risk, setRisk] = useState({ kill_switch: false });
  const [brackets, setBrackets] = useState([]);

  // form
  const [symbol, setSymbol] = useState("NIFTY");
  const [symbolSearch, setSymbolSearch] = useState("");
  const [side, setSide] = useState("BUY");
  const [qty, setQty] = useState(50);
  const [instrumentType, setInstrumentType] = useState("EQ");
  const [optStrike, setOptStrike] = useState("");
  const [optKind, setOptKind] = useState("CE");
  const [stopLoss, setStopLoss] = useState("");
  const [targetPrice, setTargetPrice] = useState("");
  // RU-OPTUI OU-5: the OPT ticket's strikes come from the underlying's chain (live only).
  const [optChain, setOptChain] = useState(null);
  const [optChainError, setOptChainError] = useState(false);
  // RU-OPTSTALE OS-4: when the chain was received (client clock), a refresh counter and a 5 s clock tick.
  const [optChainAt, setOptChainAt] = useState(null);
  const [optChainRound, setOptChainRound] = useState(0);
  const [optRefreshing, setOptRefreshing] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const isOpt = instrumentType === "OPT";

  // A new underlying starts from no chain; a refresh keeps the shown chain until the new answer arrives.
  useEffect(() => {
    if (!isOpt || !symbol) return;
    setOptChain(null);
    setOptChainAt(null);
    setOptChainError(false);
  }, [isOpt, symbol]);

  useEffect(() => {
    if (!isOpt || !symbol) return undefined;
    let cancelled = false;
    setOptRefreshing(true);
    api.get("/market/options-chain", { params: { symbol } })
      .then(({ data }) => {
        if (cancelled) return;
        const at = Date.now();
        setOptChain(data); setOptChainAt(at); setOptChainError(false); setNowMs(at);
      })
      .catch(() => { if (!cancelled) { setOptChain(null); setOptChainAt(null); setOptChainError(true); } })
      .finally(() => { if (!cancelled) setOptRefreshing(false); });
    return () => { cancelled = true; };
  }, [isOpt, symbol, optChainRound]);

  useEffect(() => {
    if (!isOpt) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(id);
  }, [isOpt]);

  const strikeChoices = liveStrikes(optChain);
  // The selected strike is dropped when the underlying changes or it is outside the new chain's window.
  useEffect(() => {
    if (optStrike !== "" && !strikeChoices.some((k) => String(k) === String(optStrike))) setOptStrike("");
  }, [optChain]); /* eslint-disable-line react-hooks/exhaustive-deps */
  const optChainInfo = chainSourceInfo(optChain, { error: optChainError });
  const optBlockedReason = !isOpt ? null
    : optChainInfo.kind === "loading" ? `Loading chain for ${symbol}…`
    : !optChainInfo.tradable ? `No live chain for ${symbol} — ${optChainInfo.label}`
    : strikeChoices.length === 0 ? `No strikes in the live chain for ${symbol}`
    : null;
  // RU-OPTSTALE OS-4: a live chain that is stale, aged past the bound, or a closed-session snapshot prices nothing.
  const optStaleNow = (atMs) => (!isOpt || optBlockedReason) ? null
    : chainOrderBlock(optChain, { error: optChainError, nowMs: atMs, receivedAtMs: optChainAt });
  const optStaleReason = optStaleNow(nowMs);
  // Evaluated again on the click: a backgrounded tab's 5 s tick may lag behind the chain's age.
  const optRefusedNow = () => {
    const why = optStaleNow(Date.now());
    if (why) { setNowMs(Date.now()); toast.error(`Order not sent: ${why}`); }
    return !!why;
  };

  const load = async () => {
    const [s, p, o, r, lb, lo, br] = await Promise.all([
      api.get("/market/symbols"),
      api.get("/paper/positions"),
      api.get("/paper/orders"),
      api.get("/risk/limits"),
      api.get("/brokers"),
      api.get("/orders/live/orders").catch(() => ({ data: { orders: [] } })),
      api.get("/paper/brackets").catch(() => ({ data: { brackets: [] } })),
    ]);
    setSymbols(s.data.symbols);
    setPositions(p.data.positions);
    setTotals({
      total_pnl: p.data.total_pnl,
      exposure: p.data.gross_exposure ?? p.data.exposure,
      net_exposure: p.data.net_exposure ?? null,
      bad_avg_price_count: p.data.bad_avg_price_count || 0,
      stale_count: p.data.stale_count || 0,
    });
    setOrders(o.data.orders);
    setRisk(r.data);
    setLiveBrokers(lb.data.items.filter((b) => b.connected && b.status === "live").map((b) => b.name));
    setLiveOrders(lo.data.orders || []);
    setBrackets(br.data.brackets || []);
  };

  useEffect(() => {
    load();
  }, []);

  const placeOrder = async () => {
    if (optRefusedNow()) return;
    try {
      const payload = {
        symbol,
        side,
        qty: Number(qty),
        order_type: "MARKET",
        instrument_type: instrumentType,
        // RU-OPTUI OU-6: a plain /paper/order creates no bracket, so an option order carries no stop / target.
        ...(!isOpt && stopLoss ? { stop_loss: Number(stopLoss) } : {}),
        ...(!isOpt && targetPrice ? { target_price: Number(targetPrice) } : {}),
      };
      if (instrumentType === "OPT") {
        payload.option_strike = Number(optStrike);
        payload.option_kind = optKind;
      }
      const { data } = await api.post("/paper/order", payload);
      if (data.idempotent_replay) {
        toast.info(`${side} ${qty} ${symbol} — replay (already filled within 24h)`);
      } else {
        toast.success(`${side} ${qty} ${symbol} filled (paper)`);
      }
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Order rejected";
      if (status === 409) {
        toast.warning(detail, {
          action: {
            label: "Force",
            onClick: async () => {
              if (optRefusedNow()) return;
              try {
                await api.post("/paper/order?force=true", {
                  symbol,
                  side,
                  qty: Number(qty),
                  order_type: "MARKET",
                  instrument_type: instrumentType,
                  ...(instrumentType === "OPT"
                    ? { option_strike: Number(optStrike), option_kind: optKind }
                    : {}),
                });
                toast.success("Forced order placed");
                load();
              } catch {
                toast.error("Force order failed");
              }
            },
          },
        });
      } else {
        toast.error(detail);
      }
    }
  };

  const flatten = async () => {
    try {
      const { data } = await api.post("/paper/flatten");
      const t = flattenToast(data);
      toast[t.tone](t.title, t.description ? { description: t.description } : undefined);
      load();
    } catch (e) {
      toast.error(flattenErrorText(e));
    }
  };

  const toggleKill = async (val) => {
    try {
      await api.put("/risk/limits", { ...risk, kill_switch: val });
      toast[val ? "warning" : "success"](val ? "Kill switch ACTIVE — orders blocked" : "Kill switch released");
      load();
    } catch {
      toast.error("Could not toggle kill switch");
    }
  };

  const pnlTone = totals.total_pnl > 0 ? "profit" : totals.total_pnl < 0 ? "loss" : "neutral";

  return (
    <AppShell>
      <PageHeader
        overline="Execution"
        title="Paper Trading Console"
        description="Simulate orders on live mock data. Multi-leg options supported. Kill switch halts new orders instantly."
        actions={
          <div className="flex items-center gap-3 panel px-3 py-2">
            <ShieldAlert className={`w-4 h-4 ${risk.kill_switch ? "txt-loss" : "txt-muted"}`} />
            <div className="overline">Kill switch</div>
            <Switch data-testid="paper-kill-switch" checked={!!risk.kill_switch} onCheckedChange={toggleKill} />
          </div>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard testid="paper-kpi-pnl" label="Open P&L" value={`₹${fmt(totals.total_pnl)}`} tone={pnlTone}
                    sub={totals.bad_avg_price_count ? `${totals.bad_avg_price_count} position(s) filled at price 0 excluded` : undefined} />
          <StatCard testid="paper-kpi-exposure" label="Gross exposure" value={`₹${fmt(totals.exposure, 0)}`}
                    sub={totals.net_exposure != null ? `net ₹${fmt(totals.net_exposure, 0)}` : undefined} />
          <StatCard testid="paper-kpi-positions" label="Open positions" value={positions.length}
                    sub={totals.stale_count ? `${totals.stale_count} position${totals.stale_count === 1 ? "" : "s"} at the last real mark (no fresh quote)` : undefined} />
          <StatCard testid="paper-kpi-mode" label="Mode" value="PAPER" sub="live trading: pending broker keys" />
        </div>

        {/* Order ticket */}
        <div className="panel p-5">
          <div className="overline mb-3">Order ticket</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-3 items-end">
            <div className="col-span-2 sm:col-span-1 lg:col-span-2">
              <Label className="overline">Symbol</Label>
              <div className="relative mt-2">
                <Input
                  data-testid="order-symbol-select"
                  value={symbolSearch || symbol}
                  onChange={(e) => { setSymbolSearch(e.target.value); }}
                  onFocus={() => setSymbolSearch("")}
                  placeholder="Search symbol…"
                  className="rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data uppercase"
                />
                {symbolSearch && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-[var(--bg-surface)] border border-[var(--border)] max-h-48 overflow-auto">
                    {symbols
                      .filter((s) => s.symbol.includes(symbolSearch.toUpperCase()))
                      .slice(0, 20)
                      .map((s) => (
                        <button
                          key={s.symbol}
                          className="w-full text-left px-3 py-2 text-sm font-mono-data hover:bg-[var(--bg-surface-2)] txt-secondary hover:text-white"
                          onMouseDown={() => { setSymbol(s.symbol); setSymbolSearch(""); }}
                        >
                          {s.symbol}
                        </button>
                      ))}
                    {symbols.filter((s) => s.symbol.includes(symbolSearch.toUpperCase())).length === 0 && (
                      <div className="px-3 py-2 text-xs txt-muted">No matches</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="overline">Side</Label>
              <Select value={side} onValueChange={setSide}>
                <SelectTrigger data-testid="order-side-select" className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)]">
                  <SelectItem value="BUY">BUY</SelectItem>
                  <SelectItem value="SELL">SELL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Type</Label>
              <Select value={instrumentType} onValueChange={setInstrumentType}>
                <SelectTrigger data-testid="order-type-select" className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)]">
                  <SelectItem value="EQ">Equity</SelectItem>
                  <SelectItem value="OPT">Option</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Qty</Label>
              <Input data-testid="order-qty-input" type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
            </div>
            {instrumentType === "OPT" && (
              <>
                <div>
                  <Label className="overline">Strike</Label>
                  <select
                    data-testid="order-strike-select"
                    value={optStrike}
                    onChange={(e) => setOptStrike(e.target.value)}
                    disabled={!!optBlockedReason}
                    className="mt-2 h-10 w-full rounded-none bg-[var(--bg-page)] border border-[var(--border)] px-2 text-sm font-mono-data disabled:opacity-50"
                  >
                    <option value="">{optBlockedReason ? "—" : "Pick strike…"}</option>
                    {strikeChoices.map((k) => (
                      <option key={k} value={String(k)}>{k}{k === optChain?.atm ? " (ATM)" : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="overline">CE/PE</Label>
                  <Select value={optKind} onValueChange={setOptKind}>
                    <SelectTrigger data-testid="order-kind-select" className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)]">
                      <SelectItem value="CE">CE (Call)</SelectItem>
                      <SelectItem value="PE">PE (Put)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {!isOpt && (
              <>
                <div>
                  <Label className="overline">Stop Loss</Label>
                  <Input
                    data-testid="order-stop-input"
                    type="number"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                    placeholder="Optional"
                    className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data"
                  />
                </div>
                <div>
                  <Label className="overline">Target</Label>
                  <Input
                    data-testid="order-target-input"
                    type="number"
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(e.target.value)}
                    placeholder="Optional"
                    className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data"
                  />
                </div>
              </>
            )}
            <Button
              data-testid="order-place-btn"
              onClick={placeOrder}
              disabled={risk.kill_switch || (isOpt && (!!optBlockedReason || !!optStaleReason || optStrike === ""))}
              className={`rounded-none h-10 font-section tracking-wider ${side === "BUY" ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-red-500 hover:bg-red-400 text-white"}`}
            >
              <ArrowDownUp className="w-4 h-4 mr-2" /> PLACE {side}
            </Button>
          </div>
          {isOpt && (
            <div data-testid="order-opt-chain" className="mt-3 flex flex-wrap items-center gap-3 text-xs font-mono-data">
              <ChainSourceBadge chain={optChain} error={optChainError} testid="order-chain-source" />
              <span data-testid="order-opt-expiry">
                <span className="txt-muted">Expiry used:</span>{" "}
                <span className="text-white">{optChainInfo.tradable && optChain?.expiry ? optChain.expiry : "—"}</span>
              </span>
              {optBlockedReason && (
                <span data-testid="order-opt-blocked" className="txt-loss">{optBlockedReason}</span>
              )}
              {optStaleReason && (
                <span data-testid="order-opt-stale" className="txt-loss">{optStaleReason}</span>
              )}
              <button
                type="button"
                data-testid="order-chain-refresh"
                onClick={() => setOptChainRound((n) => n + 1)}
                disabled={optRefreshing}
                className="border border-[var(--border)] px-2 py-0.5 txt-secondary hover:text-white disabled:opacity-40"
              >
                {optRefreshing ? "Refreshing…" : "Refresh chain"}
              </button>
              <span className="txt-muted">Stop loss / target are not placed for option orders from this ticket.</span>
            </div>
          )}
          {risk.kill_switch && (
            <div className="mt-3 text-xs txt-loss">Kill switch active — new orders blocked.</div>
          )}
        </div>

        {/* Bracket Order */}
        <BracketOrderPanel symbols={symbols} disabled={risk.kill_switch} onPlaced={load} />

        {/* Multi-leg builder */}
        <MultiLegBuilder symbols={symbols} disabled={risk.kill_switch} onPlaced={load} />

        {/* LIVE order ticket — only renders properly once a live broker exists */}
        <LiveOrderTicket
          liveBrokers={liveBrokers}
          killSwitch={risk.kill_switch}
          onPlaced={load}
        />

        <PositionsTable positions={positions} onFlatten={flatten} onRefresh={load} />

        {/* Active bracket orders */}
        {brackets.length > 0 && <BracketOrdersTable brackets={brackets} />}

        <OrdersTable orders={orders} mode="paper" />

        {liveOrders.length > 0 && (
          <div data-testid="live-orders-section">
            <OrdersTable orders={liveOrders} mode="live" />
          </div>
        )}
      </div>
    </AppShell>
  );
}
